/**
 * @file DocumentDetailPage.tsx
 * @description Карточка документа CRM (phase09; phase19.31 — зона «Клиенты и
 * сделки», часть 3, по образцу документов кабинета, uiux-design §5).
 *
 * Страница — раскладка из частей `components/crm/documents/`:
 *  - шапка в верхней панели (`CabinetPageHeader`): «‹ к документам», номер
 *    (у черновика — «Черновик: <вид>»), вид подписью, статус пилюлей;
 *    главное действие по статусу (черновик → «Выпустить», выпущен →
 *    «Отметить подписанным», подписан → «Скачать PDF»), на узком экране —
 *    в нижней полосе; остальные — видимыми кнопками строкой под шапкой на
 *    всех ширинах (SC67), опасное последним (в панели рядом с главной они
 *    обрезали номер документа на 1280);
 *  - полосы: незаполненные реквизиты, причина аннулирования, что значит
 *    черновик;
 *  - сведения (`DocumentMetaGrid`), позиции с итогом, файлы.
 * Подтверждения — окнами кабинета; аннулирование — с обязательной причиной.
 */

import { useNavigate, useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { documentActionPlan } from "../../../../components/crm/documents/document-actions";
import { DocumentStatusPill } from "../../../../components/crm/documents/document-cells";
import { buildDocumentMeta } from "../../../../components/crm/documents/document-meta";
import { documentCounterparty, documentPageTitle } from "../../../../components/crm/documents/document-view";
import { DocumentFilesSection } from "../../../../components/crm/documents/DocumentFilesSection";
import { DocumentPrimaryAction, DocumentSecondaryActions } from "../../../../components/crm/documents/DocumentHeaderActions";
import { DocumentItemsSection } from "../../../../components/crm/documents/DocumentItemsSection";
import { LegalProfileNotice } from "../../../../components/crm/documents/LegalProfileNotice";
import { useDocumentActions } from "../../../../components/crm/documents/useDocumentActions";
import { useDocumentCard } from "../../../../components/crm/documents/useDocumentCard";
import { ReasonDialog } from "../../../../components/orders/ReasonDialog";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { DocumentMetaGrid } from "../../../../components/ui/DocumentMetaGrid";
import { ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { NoticeStrip } from "../../../../components/ui/NoticeStrip";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import { DOCUMENT_KIND_LABEL, type DocumentDto } from "../../../../lib/api-crm";
import { formatShopDate } from "../../../../lib/shop-time";

const VOID_TEXTS = {
  title: "Аннулировать документ?",
  description: "Документ останется в реестре со своим номером и статусом «Аннулирован». Отменить аннулирование нельзя.",
  label: "Причина",
  placeholder: "Например: ошибка в реквизитах покупателя",
  confirm: "Аннулировать",
  cancel: "Не аннулировать",
};

/** Пояснение под номером: вид и покупатель (у черновика вид уже в названии). */
function subtitleOf(doc: DocumentDto): string | undefined {
  const kind = doc.number ? DOCUMENT_KIND_LABEL[doc.kind] : null;
  return [kind, documentCounterparty(doc)].filter(Boolean).join(" · ") || undefined;
}

export function DocumentDetailPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const base = `/shop/${slug}`;
  const card = useDocumentCard(id);
  const back = { to: `${base}/crm/documents`, label: "К документам" };

  if (!card.doc) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Документ" back={back} />
        {card.loading ? <ListSkeleton rows={4} compact /> : <ListError message={card.error ?? "Документ не найден"} onRetry={card.refresh} />}
      </div>
    );
  }
  return <DocumentCard doc={card.doc} base={base} back={back} replace={card.replace} />;
}

interface CardProps {
  doc: DocumentDto;
  base: string;
  back: { to: string; label: string };
  replace: (doc: DocumentDto) => void;
}

/** Карточка загруженного документа: хуки действий живут при готовом документе. */
function DocumentCard({ doc, base, back, replace }: CardProps) {
  const navigate = useNavigate();
  const canIssue = useHasPermission("SHOP_ISSUE_DOCUMENTS");
  const canDelete = useHasPermission("SHOP_DELETE_DOCUMENTS");
  const canOpenOrder = useHasPermission("SHOP_VIEW_ORDERS");
  const canOpenDeal = useHasPermission("SHOP_USE_CRM");
  const canOpenCustomer = useHasPermission("SHOP_VIEW_CUSTOMERS");
  const actions = useDocumentActions({ doc, replace, onDeleted: () => navigate(back.to) });
  const plan = documentActionPlan({ status: doc.status, hasPdf: !!doc.pdfStorageKey, canIssue, canDelete });

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={documentPageTitle(doc)}
        subtitle={subtitleOf(doc)}
        status={<DocumentStatusPill status={doc.status} />}
        back={back}
        action={plan.primary ? <DocumentPrimaryAction plan={plan} busy={actions.busy} onRun={actions.run} /> : undefined}
      />
      {/* Остальные кнопки — строкой под шапкой на всех ширинах: в панели три
          кнопки рядом с главной на 1280 обрезали номер документа до «УПД-2026…». */}
      {plan.secondary.length > 0 ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Действия с документом">
          <DocumentSecondaryActions plan={plan} busy={actions.busy} onRun={actions.run} />
        </div>
      ) : null}

      {doc.legalProfileIncomplete && doc.status !== "VOIDED" ? (
        <LegalProfileNotice base={base} gaps={doc.legalProfileGaps ?? []} issued={doc.status !== "DRAFT"} />
      ) : null}
      {doc.status === "VOIDED" ? (
        <NoticeStrip tone="error" title={`Аннулирован${doc.voidedAt ? ` ${formatShopDate(doc.voidedAt)}` : ""}`}>
          {doc.voidReason ? `Причина: ${doc.voidReason}` : "Причина не указана"}
        </NoticeStrip>
      ) : null}
      {doc.status === "DRAFT" ? (
        <NoticeStrip tone="info">Черновик: номер документа и PDF появятся после выпуска.</NoticeStrip>
      ) : null}

      <DocumentMetaGrid items={buildDocumentMeta(doc, { base, canOpenOrder, canOpenDeal, canOpenCustomer })} />
      {doc.notes ? <NoticeStrip tone="info" title="Комментарий">{doc.notes}</NoticeStrip> : null}

      <DocumentItemsSection doc={doc} />
      <DocumentFilesSection
        doc={doc}
        canRegenerate={canIssue && doc.status !== "VOIDED"}
        busy={actions.busy}
        onOpen={actions.openPdf}
        onRegenerate={actions.regenerate}
      />

      <ConfirmDialog request={actions.confirm.request} onSettle={actions.confirm.settle} />
      <ReasonDialog
        open={actions.voiding}
        texts={VOID_TEXTS}
        busy={actions.busy === "void"}
        danger
        onClose={actions.closeVoid}
        onConfirm={actions.confirmVoid}
      />
    </div>
  );
}
